import { getFileExtension, parseImageFromFile } from "@faro-lotv/flat-ui";
import { assert } from "@faro-lotv/foundation";

/** Supported image file extensions for picking image files */
const PICK_IMAGE_FILES_DIALOG_FILTER = [
  ".jpg",
  ".jpeg",
  ".png",
  ".heic",
  ".heif",
].join(",");

/**
 * Prompts the user to select a single file using a file dialog.
 *
 * @param filter a string listing the extensions for the selected file (e.g., ".jpg,.jpeg,.png", or ".csv").
 * @returns A promise for File to the selected file, or `null` if the user cancels the dialog.
 */
export function pickSingleFile(filter: string): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = filter;
    input.multiple = false;

    input.onchange = () => {
      const file = input.files?.[0] ?? null;
      resolve(file);
    };

    input.oncancel = () => {
      resolve(null);
    };

    input.click();
  });
}

/**
 * Prompts the user to select multiple files using a file dialog.
 *
 * @param filter a string listing the extensions for the selected file (e.g., ".jpg,.jpeg,.png", or ".csv").
 * @returns A promise for FileList to the selected files, or `null` if the user cancels the dialog.
 */
export function pickMultipleFiles(filter: string): Promise<FileList | null> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = filter;
    input.multiple = true;

    input.onchange = () => {
      const files = input.files ?? null;
      resolve(files);
    };

    input.click();
  });
}

/**
 * Checks if a file has a HEIC or HEIF extension.
 *
 * @param filename - The name of the file to check
 * @returns `true` if the file extension is ".heic" or ".heif" (case-insensitive), `false` otherwise
 */
function hasHeicHeifFileExtension(filename: string): boolean {
  const extension = getFileExtension(filename)?.toLowerCase();
  return extension === "heic" || extension === "heif";
}

/**
 * Converts a HEIC or HEIF image file to JPEG format while preserving some data from the EXIF metadata.
 *
 * @param file - The HEIC or HEIF image file to convert. Must have a .heic or .heif extension.
 * @returns A Promise that resolves to a new File object containing the JPEG image with preserved metadata.
 *          The filename will have the original extension replaced with .jpg.
 * @throws Will throw an error if the conversion process fails.
 */
async function convertHeicHeifToJpeg(file: File): Promise<File> {
  const { isHeic, heicTo } = await import("heic-to");
  const { parseMetadata, writeMetadata } = await import("@uswriting/exiftool");
  assert(
    hasHeicHeifFileExtension(file.name),
    "File must have a .heic or .heif extension.",
  );
  if (!(await isHeic(file))) {
    throw new Error(
      `${file.name} is corrupted or uses an unsupported version of the format.`,
    );
  }

  // Convert HEIC/HEIF to JPEG
  const heicResult = await heicTo({
    blob: file,
    type: "image/jpeg",
    quality: 1,
  });

  const jpgFileBlob = Array.isArray(heicResult) ? heicResult[0] : heicResult;
  const jpegFileName = file.name.replace(/\.(heic|heif)$/i, ".jpg");
  const convertedJpegFile = new File([jpgFileBlob], jpegFileName, {
    type: "image/jpeg",
  });

  // Selection of commonly used tags to preserve
  const tagsToPreserve = [
    "-Orientation",
    "-DateTimeOriginal",
    "-CreateDate",
    "-GPSLatitude",
    "-GPSLongitude",
    "-Make",
    "-Model",
  ];

  // Read EXIF metadata from original file and save them in the converted JPEG
  const parseResult = await parseMetadata(file, {
    args: ["-json", "-n", ...tagsToPreserve],
    transform: (data) => JSON.parse(data),
  });
  assert(parseResult.success, "Failed to parse metadata from HEIC/HEIF file.");
  if (parseResult.data === undefined || parseResult.data.length === 0) {
    // No metadata to write, return converted file as is
    return convertedJpegFile;
  }

  const metadata = parseResult.data[0];
  // Remove the SourceFile tag to avoid issues when writing metadata
  delete metadata.SourceFile;

  // Write metadata to converted JPEG file
  const writeResult = await writeMetadata(convertedJpegFile, metadata);
  assert(
    writeResult.success,
    "Failed to write metadata to converted JPEG file.",
  );
  return new File([writeResult.data], jpegFileName, { type: "image/jpeg" });
}

/**
 * Validates an image file and converts it if necessary to ensure it's natively supported.
 *
 * @param file - The image file to process
 * @returns A promise that resolves to a validated image file in a natively supported format
 * @throws {Error} If conversion to native format fails or if the file is not a valid image
 */
async function getNativelySupportedImageFile(file: File): Promise<File> {
  let finalFile = file;
  if (hasHeicHeifFileExtension(file.name)) {
    try {
      // Convert HEIC/HEIF to JPEG
      finalFile = await convertHeicHeifToJpeg(file);
    } catch {
      throw new Error(`Error converting ${file.name} to JPEG.`);
    }
  }

  // Validate that the file is a valid image file by attempting to parse it
  try {
    await parseImageFromFile(finalFile);
  } catch {
    throw new Error(`${finalFile.name} is not valid image file.`);
  }

  return finalFile;
}

/**
 * Prompts the user to select a single image file using a file dialog.
 * Throws an error if no valid image was selected.
 *
 * @returns A promise for File to the selected image file.
 */
export async function pickImageFile(): Promise<File> {
  const file = await pickSingleFile(PICK_IMAGE_FILES_DIALOG_FILTER);
  if (!file) {
    throw new Error("No file was selected.");
  }

  return getNativelySupportedImageFile(file);
}

/**
 * Prompts the user to select a single or multiple image file using a file dialog.
 * Throws an error if not all images are valid or no file was selected.
 *
 * @returns A promise for File to the selected image file.
 */
export async function pickImageFiles(): Promise<File[]> {
  const fileList = await pickMultipleFiles(PICK_IMAGE_FILES_DIALOG_FILTER);

  if (!fileList?.length) {
    throw new Error("No file was selected.");
  }

  const files = Array.from(fileList);
  for (let index = 0; index < files.length; index++) {
    files[index] = await getNativelySupportedImageFile(files[index]);
  }

  return files;
}
